import { api } from '../api/client.ts';

/**
 * Tells the proxy as soon as this device stops playing a stream session.
 *
 * Providers allow few connections at once -- often two. The proxy would
 * notice an abandoned session only after a few seconds without reads, and the
 * next channel waits for exactly that connection. A player holds the sessions
 * it played until it is destroyed; once no player on this device holds one,
 * the proxy is told after a short grace, which covers a tile moving to
 * another slot and asking for the same session again.
 */
const GRACE_MS = 1500;

const holders = new Map<string, number>();
const pending = new Map<string, number>();

export function hold(sessionId: string): void {
  holders.set(sessionId, (holders.get(sessionId) ?? 0) + 1);
  const timer = pending.get(sessionId);
  if (timer !== undefined) {
    clearTimeout(timer);
    pending.delete(sessionId);
  }
}

export function drop(sessionId: string): void {
  const n = (holders.get(sessionId) ?? 0) - 1;
  if (n > 0) {
    holders.set(sessionId, n);
    return;
  }
  holders.delete(sessionId);
  if (pending.has(sessionId)) return;
  pending.set(sessionId, window.setTimeout(() => {
    pending.delete(sessionId);
    if (holders.has(sessionId)) return;
    // Failing costs nothing: the proxy still collects it once reads stop.
    void api.releaseSession(sessionId).catch(() => undefined);
  }, GRACE_MS));
}

// Closing the app or the tab: free everything at once, the requests of a
// page being unloaded only survive as beacons.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    for (const id of new Set([...holders.keys(), ...pending.keys()])) {
      navigator.sendBeacon?.(`/api/sessions/${encodeURIComponent(id)}/release`);
    }
  });
}
